import { Select, type SelectItem } from '@/components/inputs/Select'
import type { ControlSize } from '@/components/inputs/control-size'

/**
 * A value of a {@link ChoiceSelect} and its label.
 */
export type Choice<T> = readonly [value: T, label: string]

export interface ChoiceSelectProps<T> {
  value: T
  choices: readonly Choice<T>[]
  onChange: (value: T) => unknown
  placeholder?: string
  isDisabled?: boolean
  isInvalid?: boolean
  /**
   * `small` fits a sentence, like "Matches ALL of the following".
   */
  size?: ControlSize
  'aria-label'?: string
  testId?: string
}

/**
 * Picks one of a fixed set of values, which may be booleans or other values
 * `Select` can't use as keys.
 */
export function ChoiceSelect<T>({
  value,
  choices,
  onChange,
  placeholder,
  isDisabled,
  isInvalid,
  size,
  'aria-label': ariaLabel,
  testId,
}: ChoiceSelectProps<T>) {
  // `Select` treats falsy values as unselected, so items are keyed by index.
  const items: SelectItem<string>[] = choices.map(([, label], i) => [
    String(i),
    label,
  ])
  const index = choices.findIndex(([choice]) => choice === value)
  return (
    <Select
      value={index === -1 ? undefined : String(index)}
      items={items}
      onChange={(e) => onChange(choices[parseInt(e.target.value, 10)][0])}
      placeholder={placeholder ?? null}
      isDisabled={isDisabled}
      status={isInvalid ? 'danger' : 'default'}
      size={size}
      title={choices[index]?.[1]}
      aria-label={ariaLabel}
      data-test={testId}
    />
  )
}
